import React, { useCallback, useRef, useEffect, useState, memo } from 'react';
import { MoonLoader } from 'react-spinners';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../../contexts/SidebarContext';
import { useMobile } from '../../contexts/MobileContext';

import styles from './styles/ChatsList.module.css';

const groupChatsByDate = chats => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const groups = {
        'Today': [],
        'Yesterday': [],
        'Previous 7 Days': [],
        'Previous 30 Days': [],
    };

    const getMonthYearKey = date => {
        return date.toLocaleString('default', { month: 'long', year: 'numeric' });
    };

    chats.forEach(chat => {
        const chatDate = new Date(chat.updatedAt);
        const diffTime = Math.floor((today - chatDate) / (1000 * 60 * 60 * 24));

        if (chatDate.toDateString() === today.toDateString()) {
            groups['Today'].push(chat);
        } else if (chatDate.toDateString() === yesterday.toDateString()) {
            groups['Yesterday'].push(chat);
        } else if (diffTime <= 7) {
            groups['Previous 7 Days'].push(chat);
        } else if (diffTime <= 30) {
            groups['Previous 30 Days'].push(chat);
        } else {
            const monthYearKey = getMonthYearKey(chatDate);
            if (!groups[monthYearKey]) {
                groups[monthYearKey] = [];
            }
            groups[monthYearKey].push(chat);
        }
    });

    return groups;
};

const ChatsList = ({
    chats,
    isLoading,
    error,
    selectedChatId,
    setSelectedChatId,
    onLoadMore,
    hasMore,
    containerRef = null,
}) => {
    const navigate = useNavigate();
    const { closeSidebar } = useSidebar();
    const { isMobile } = useMobile();
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const loaderRef = useRef(null);

    const handleIntersection = useCallback(entries => {
        const target = entries[0];

        // Check internet connection
        if (!navigator.onLine) {
            return;
        }

        if (target.isIntersecting && hasMore && !isLoadingMore) {
            setIsLoadingMore(true);
            onLoadMore().finally(() => setIsLoadingMore(false));
        }
    }, [hasMore, isLoadingMore, onLoadMore]);

    // Set up intersection observer
    useEffect(() => {
        const options = {
            root: containerRef.current,
            rootMargin: '200px', // Start loading 200px before reaching the end
            threshold: 0,
        };

        const observer = new IntersectionObserver(handleIntersection, options);

        if (loaderRef.current) {
            observer.observe(loaderRef.current);
        }

        return () => observer.disconnect();
    }, [handleIntersection, containerRef]);

    const handleChatClick = useCallback(chatId => {
        setSelectedChatId(chatId);
        navigate(`/c/${chatId}`);

        // Close sidebar on mobile when selecting a chat
        if (isMobile) {
            closeSidebar();
        }
    }, [navigate, setSelectedChatId, isMobile, closeSidebar]);

    const groupedChats = groupChatsByDate(chats);

    if (chats?.length === 0 && !isLoading) {
        return (
            <p className={styles.noChats}>No chats yet</p>
        )
    }

    return (
        <div className={styles.chatsList}>
            {isLoading && !isLoadingMore && (
                <div className={styles.loaderContainer}>
                    <MoonLoader color="#000000" size={30} />
                </div>
            )}
            {error && <div className={styles.error}>{error}</div>}
            {Object.entries(groupedChats).map(([group, chats]) => (
                chats.length > 0 && (
                    <div key={group} className={styles.chatsGroup}>
                        <h3 className={styles.groupTitle}>{group}</h3>
                        {chats.map(chat => (
                            <a
                                key={chat.id} 
                                className={`${styles.chatItem} ${chat.id === selectedChatId ? styles.selected : ''}`} 
                                onClick={e => {
                                    e.preventDefault();
                                    handleChatClick(chat.id);
                                }}
                                href={`/c/${chat.id}`}
                            >
                                <div className={styles.chatItemContent} title={chat.title}>
                                    {chat.title}
                                    <div className={styles.chatItemGradient}></div>
                                </div>
                            </a>
                        ))}
                    </div>
                )
            ))}

            {hasMore && (
                <div ref={loaderRef} className={styles.bottomLoader}>
                    {isLoadingMore && (
                        <MoonLoader color="#000000" size={20} />
                    )}
                </div>
            )}
        </div>
    );
};

export default memo(ChatsList);
